import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { ulid } from 'ulid';
import type { CSSProperties } from 'react';
import {
  type Block,
  type BlockStyle,
  type DesignTokens,
  designTokenVar,
  type TableBlock,
} from '@blogforge/shared';
import type { DragData } from './types';

export const pathDepth = (drag: DragData) => drag.path.length;
export const isSameDragParent = (a: DragData, b: DragData) =>
  a.path.length === b.path.length &&
  a.path.every(
    (seg, i) =>
      seg.id === b.path[i]?.id && seg.type === b.path[i].type && seg.column === b.path[i].column,
  );
export const parentId = (data: DragData): string | null =>
  data.path.length > 0 ? (data.path[data.path.length - 1]?.id ?? null) : null;

/**
 * Parse a document's `designOverrides` column into token key/value pairs.
 * The API returns it as a JSON string, but callers that read it from an
 * already-decoded payload (exports, imports, tests) hand over an object, and a
 * malformed value must never break the editor — so anything unparseable is
 * treated as "no overrides". Entries are preserved verbatim: the inspector
 * writes this map back, so dropping keys it does not understand would lose
 * plugin-defined tokens.
 */
export function parseDesignOverrides(raw: unknown): DesignTokens {
  if (typeof raw === 'string') {
    try {
      return parseDesignOverrides(JSON.parse(raw) as DesignTokens);
    } catch {
      return {};
    }
  }
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as DesignTokens) : {};
}

/**
 * Whether `value` is a colour the browser can actually paint. Document colours
 * are free-text in the inspector, and `background: var(--doc-color-background,
 * …)` with an invalid substituted value is invalid *at computed-value time* — the
 * fallback would NOT kick in, the background would just go transparent. So gate
 * on `CSS.supports` and fall back to the caller's own default instead.
 */
export function isCssColor(value: string | undefined): value is string {
  if (!value) return false;
  return typeof CSS !== 'undefined' && typeof CSS.supports === 'function'
    ? CSS.supports('color', value)
    : /^#[0-9a-f]{3,8}$|^(rgb|hsl)a?\(/i.test(value.trim());
}

/**
 * Whether the browser can paint `value` as a `background-image`.
 *
 * `block.style.backgroundGradient` holds a whole CSS background-image value
 * (normally `linear-gradient(…)`, but `url(…)` is equally legitimate), and the
 * inspector takes it as free text. A malformed value is dropped by the browser
 * without any error — the block would simply look unstyled — so the field warns
 * on anything that is not a gradient or an image url instead of implying it
 * worked. Same `CSS.supports`-first approach as `isCssColor`.
 */
export function isCssBackgroundImage(value: string | undefined): value is string {
  const trimmed = value?.trim();
  if (!trimmed) return false;
  return typeof CSS !== 'undefined' && typeof CSS.supports === 'function'
    ? CSS.supports('background-image', trimmed)
    : /^(repeating-)?(linear|radial|conic)-gradient\(|^url\(/i.test(trimmed);
}

/** Whether the browser accepts `value` as a length (used for sizes/spacing/radii). */
function isCssLength(value: string): boolean {
  return typeof CSS !== 'undefined' && typeof CSS.supports === 'function'
    ? CSS.supports('width', value)
    : /^-?\d*\.?\d+(px|rem|em|%|vh|vw|pt|ch|ex)?$/i.test(value.trim());
}

/** Whether `value` is usable as a `font-family` / `line-height` value. */
function isCssFontFamily(value: string): boolean {
  return typeof CSS !== 'undefined' && typeof CSS.supports === 'function'
    ? CSS.supports('font-family', value)
    : // A font stack: identifiers, quoted names, commas and spaces — and at
      // least one letter, since a bare number (`1`) is not a family name.
      /^[\w\s,'"-]+$/.test(value.trim()) && /[a-z]/i.test(value);
}

function isCssLineHeight(value: string): boolean {
  return typeof CSS !== 'undefined' && typeof CSS.supports === 'function'
    ? CSS.supports('line-height', value)
    : isCssLength(value);
}

/**
 * The CSS custom property names the canvas paints a document with.
 *
 * These are the *document's* tokens, deliberately `--doc-`prefixed rather than
 * named exactly like the built page's `:root` (which `designTokenVar` gives you):
 * the editor styles its own chrome with `--color-*` too, and the two sets
 * overlap — `colorPrimary` is both the document's accent and `.btn-solid`'s
 * fill. Publishing an unprefixed token on the canvas would repaint editor
 * controls (drop zones, handles, badges) with document colours. The mapping
 * stays mechanical, so a token added to the theme is available here by name.
 */
export const documentVar = (key: string): string => `--doc-${designTokenVar(key).slice(2)}`;

/** The content column width, which is a build input rather than a token. */
export const CANVAS_WIDTH_VAR = '--canvas-width';

/**
 * Which CSS property a token's value has to be valid for, inferred from the
 * token name (`colorPrimary` → a colour, `fontBody` → a font stack, …). Returns
 * null for names we cannot type, e.g. plugin-defined tokens.
 */
function tokenProperty(key: string): 'color' | 'length' | 'font-family' | 'line-height' | null {
  // A trailing `Base` is the cascade's "root value" convention (fontSizeBase,
  // spacingBase) and says nothing about the kind — strip it before matching.
  const bare = key.replace(/Base$/, '');
  if (/color|background/i.test(bare)) return 'color';
  if (/^fontsize/i.test(bare)) return 'length';
  if (/^lineheight/i.test(bare)) return 'line-height';
  if (/^font/i.test(bare)) return 'font-family';
  if (/(size|width|height|spacing|radius|padding|margin)$/i.test(bare)) return 'length';
  return null;
}

/**
 * An inline style carrying CSS custom properties. `CSSProperties` so React
 * accepts it as a `style`, intersected with an index signature so callers (and
 * tests) can read a `--doc-…` key without a cast.
 */
export type DocumentStyle = CSSProperties & Record<string, string>;

/**
 * Turn a document's resolved design into the inline style the canvas publishes:
 * one CSS variable per token, plus `--canvas-width` for the content column the
 * build renders at (a build input, not a token).
 *
 * Every value is checked before it is published, because a token's value is
 * free text from the inspector and a `var()` whose substituted value is invalid
 * is invalid *at computed-value time*: `var(--doc-color-text, fallback)` would
 * NOT fall back, the declaration would be dropped and the property would jump
 * to its initial value. Leaving the variable unset instead lets the canvas's own
 * default win, so an unusable token degrades to editor defaults rather than to
 * unstyled text.
 */
export function documentDesignVars(
  design: { tokens: Record<string, string>; width: number } | undefined,
): DocumentStyle | undefined {
  if (!design) return undefined;

  const vars: Record<string, string> = {};

  for (const [key, raw] of Object.entries(design.tokens ?? {})) {
    const value = typeof raw === 'string' ? raw.trim() : '';
    if (!value || /[;{}<>]|url\(|expression\(/i.test(value)) continue;

    const property = tokenProperty(key);
    const valid =
      property === 'color'
        ? isCssColor(value)
        : property === 'length'
          ? isCssLength(value)
          : property === 'font-family'
            ? isCssFontFamily(value)
            : property === 'line-height'
              ? isCssLineHeight(value)
              : true; // untyped token (plugin-defined): pass through, minus the guards above

    if (valid) vars[documentVar(key)] = value;
  }

  // The content column matches the width the build renders at. The document's
  // body colour needs no alias: it is already published above as
  // `--doc-color-background`, which the paper paints itself with.
  if (Number.isFinite(design.width) && design.width > 0) {
    vars[CANVAS_WIDTH_VAR] = `${Math.round(design.width)}px`;
  }

  return Object.keys(vars).length > 0 ? (vars as DocumentStyle) : undefined;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * The part of a block's `style` a canvas preview can show, as React style.
 *
 * A renderer — web or email — puts this bag inline on a wrapper, so the same
 * keys reach the published page and the sent message: email through
 * `emailInlineStyle` (themes/minimal/src/email/style.ts), web through
 * `inlineStyle`. A canvas renderer that ignores the bag therefore previews
 * something the output will not do, which is how the border control ended up
 * looking broken on the image, table, section and columns blocks.
 *
 * Only the keys that are set come back, so spreading this cannot overwrite a
 * canvas's own layout defaults with `undefined`.
 *
 * Left out on purpose:
 *   · the text properties (font*, letterSpacing, textTransform) — they belong
 *     to the element that renders the text, where it is edited;
 *   · `flexDirection` and `gap` — the email renderer drops them
 *     (EMAIL_UNSAFE_STYLE_KEYS), and a preview should not promise a layout the
 *     message will not have;
 *   · `objectFit`, for callers that size an image themselves — pass it on to
 *     the <img> as the email renderer does.
 */
export function canvasBlockStyle(style: BlockStyle | undefined): CSSProperties {
  const css: CSSProperties = {};
  if (!style) return css;

  const set = <K extends keyof CSSProperties>(key: K, value: CSSProperties[K] | undefined) => {
    if (value !== undefined && value !== '') css[key] = value;
  };

  set('color', style.color);
  set('backgroundColor', style.backgroundColor);
  set('backgroundImage', style.backgroundGradient);
  set('border', style.border);
  set('borderColor', style.borderColor);
  set('borderRadius', style.borderRadius);
  set('borderTopLeftRadius', style.borderTopLeftRadius);
  set('borderTopRightRadius', style.borderTopRightRadius);
  set('borderBottomLeftRadius', style.borderBottomLeftRadius);
  set('borderBottomRightRadius', style.borderBottomRightRadius);
  set('boxShadow', style.boxShadow);
  set('paddingTop', style.paddingTop);
  set('paddingRight', style.paddingRight);
  set('paddingBottom', style.paddingBottom);
  set('paddingLeft', style.paddingLeft);
  set('marginTop', style.marginTop);
  set('marginRight', style.marginRight);
  set('marginBottom', style.marginBottom);
  set('marginLeft', style.marginLeft);
  set('minHeight', style.minHeight);
  set('maxHeight', style.maxHeight);
  set('minWidth', style.minWidth);
  set('maxWidth', style.maxWidth);
  set('textAlign', style.textAlign);
  set('objectFit', style.objectFit);

  return css;
}

/**
 * Deep-clone a set of blocks with fresh ids — including blocks nested inside
 * sections, columns and accordions. Used when inserting a reusable component
 * (each document gets its own editable copy) and when a component update is
 * pushed onto a document.
 */
export function cloneBlocksWithNewIds(blocks: Block[]): Block[] {
  const clone = (block: Block): Block => {
    const next: Block = { ...block, id: ulid() };
    if (block.type === 'section') {
      const nested = (block.attrs.blocks as Block[] | undefined) ?? [];
      next.attrs = { ...block.attrs, blocks: nested.map(clone) };
    } else if (block.type === 'columns') {
      const columns = (block.attrs.columnBlocks as Block[][] | undefined) ?? [];
      next.attrs = {
        ...block.attrs,
        columnBlocks: columns.map((col) => ((col as Block[] | undefined) ?? []).map(clone)),
      };
    } else if (block.type === 'accordion') {
      const items = (block.attrs.items as { section: Block }[] | undefined) ?? [];
      next.attrs = {
        ...block.attrs,
        items: items.map((item) => ({ ...item, section: clone(item.section) })),
      };
    }
    return next;
  };
  return blocks.map(clone);
}

/**
 * One-line description of a table block ("3 columns × 4 rows"), for the places
 * that summarise a block in text — the reusable-component list and the AI
 * draft preview. A table has no prose of its own, so `content` is usually
 * empty and the shape of the data is the only useful thing to show.
 *
 * The column count is the widest of the headers and the body rows rather than
 * `headers.length`, because authored tables are routinely jagged and the
 * renderers pad every row out to that width.
 */
export function tableSummary(block: TableBlock): string {
  const headers = block.attrs?.headers ?? [];
  const rows = block.attrs?.rows ?? [];
  const columns = Math.max(headers.length, ...rows.map((row) => (row ?? []).length), 0);
  return `${columns} column${columns === 1 ? '' : 's'} × ${rows.length} row${rows.length === 1 ? '' : 's'}`;
}
